import { Component, computed, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { TagModule } from "primeng/tag";
import { SelectModule } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { VoilationDetailsModalComponent } from "./voilation-details-modal/voilation-details-modal.component";
import { CountUpDirective } from "../../../Utils/_directives/count-up.directive";
import { VoilationReportService } from "../../../Utils/_services/admin/voilation-reports/voilation-report.service";
import { AdminService } from "../../../Utils/_services/admin/admin/admin.service";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { IViolationReport, ModerationActionPayload, StatCard } from "../../../Utils/_models/voilation-reports.model";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";



@Component({
  selector: "app-voilation-reports",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TagModule,
    VoilationDetailsModalComponent,
    CountUpDirective,
    SearchBoxComponent,
    SpinnerLoaderComponent,
    SelectModule
  ],
  templateUrl: "./voilation-reports.component.html",
  styleUrl: "./voilation-reports.component.css",
})
export class VoilationReportsComponent implements OnInit {
  openMenuId = signal<number | null>(null);
  selectedReport = signal<IViolationReport | null>(null);
  isViewDetailsOpen = signal<boolean>(false);

  loading = signal(false);
  reports = signal<IViolationReport[]>([]);
  constructor(private confirmationService: ConfirmService,
    private violationReportsService: VoilationReportService,
    private adminService: AdminService,
    private toastr: ToastrService
  ) { }


  ngOnInit(): void {
    this.fetchStats();
    this.fetchReports();
  }

  stats = signal<StatCard[]>([
    { icon: "pi pi-exclamation-circle", count: 0, label: "Total Reports", color: "#FF6B9D" },
    { icon: "pi pi-comment", count: 0, label: "Pending Review", color: "#FF6B9D" },
    { icon: "pi pi-exclamation-triangle", count: 0, label: "Suspended Users", color: "#FFA500" },
    { icon: "pi pi-ban", count: 0, label: "Banned Users", color: "#FF0000" },
  ]);

  fetchStats(): void {
    this.violationReportsService.getStats().subscribe({
      next: (res) => {
        if (res?.data) {
          this.stats.set([
            { icon: "pi pi-exclamation-circle", count: res.data.totalReports, label: "Total Reports", color: "#FF6B9D" },
            { icon: "pi pi-comment", count: res.data.pendingReview, label: "Pending Review", color: "#FF6B9D" },
            { icon: "pi pi-exclamation-triangle", count: res.data.suspendedUsers, label: "Suspended Users", color: "#FFA500" },
            { icon: "pi pi-ban", count: res.data.bannedUsers, label: "Banned Users", color: "#FF0000" },
          ]);
        }
      },
      error: (err) => {
        console.error('Failed to fetch stats', err);
      }
    });
  }

  allSelected = signal<boolean>(false);
  includeDeleted = signal<boolean>(false);
  hasSelection = signal<boolean>(false);
  hasDeletedSelection = signal<boolean>(false);
  searchValue = signal<string>("");
  reportReasonFilter = signal<string | null>(null);
  reportReasonOptions = [
    { label: 'Bullying or harassment', value: 'bullying' },
    { label: 'Spam', value: 'spam' },
    { label: 'Pretending to be someone else', value: 'impersonation' },
    { label: 'Inappropriate content', value: 'inappropriate_content' },
    { label: 'Harassment', value: 'harassment' },
    { label: 'Appeal', value: 'appeal', disabled: true },
    { label: 'Other', value: 'other' }
  ];
  currentPage = signal(1);
  pageSizes = [5, 10, 20, 50];
  pageSize = 5;
  totalCount = signal(0);
  totalPages = 0;
  startIndex = 1;
  endIndex = 5;

  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);
  originalReports = signal<IViolationReport[]>([]);

  private allReports = signal<IViolationReport[]>([]);



  fetchReports(): void {
    this.loading.set(true);

    this.violationReportsService
      .getReports({
        page: this.currentPage(),
        limit: this.pageSize,
        search: this.searchValue(),
        reportReason: this.reportReasonFilter() || undefined,
        includeDeleted: this.includeDeleted()
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          const all = res.data?.reports || [];
          this.allReports.set(all);
          this.originalReports.set([...all]);

          const fetchedTotal = res.data?.totalCount || all.length;
          this.totalCount.set(fetchedTotal);

          if (this.currentSortField() && this.currentSortOrder()) {
            this.sortReportsLocal();
          } else {
            this.updatePagination();
            this.applyPage();
          }
        },
        error: err => {
          this.toastr.error(
            err?.error?.Errors?.message ||
            'Failed to load reports'
          );
          this.allReports.set([]);
          this.originalReports.set([]);
          this.totalCount.set(0);
          this.updatePagination();
          this.applyPage();
        }
      });
  }

  applyPage(): void {
    // The backend already paginates the data based on limit and skip, 
    // so allReports contains exactly the current page's results.
    this.reports.set(this.allReports());
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize);

    this.startIndex =
      (this.currentPage() - 1) * this.pageSize + 1;

    this.endIndex = Math.min(
      this.currentPage() * this.pageSize,
      this.totalCount()
    );
  }
  onSearch(term: string): void {
    this.searchValue.set(term);
    this.currentPage.set(1);
    this.fetchReports();
  }

  onReportReasonFilterChange(): void {
    this.currentPage.set(1);
    this.fetchReports();
  }


  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage.set(1);
    this.fetchReports();
  }
  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.fetchReports();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.fetchReports();
    }
  }

  toggleSelectAll(): void {
    const next = !this.allSelected();
    this.allSelected.set(next);
    this.allReports.update(list => {
      list.forEach(r => {
        r.selected = next;
      });
      return [...list];
    });
    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    const all = this.allReports();
    this.allSelected.set(all.length > 0 && all.every(r => r.selected));
    this.hasSelection.set(all.some(r => r.selected));
    this.hasDeletedSelection.set(all.some(r => r.selected && !!r.deletedAt));
    this.allReports.set([...all]);
  }

  getStatusSeverity(status: string): string {
    switch (status?.toLowerCase()) {
      case "pending": return "p-tag-warning";
      case "under review":
      case "reviewed": return "p-tag-info";
      case "suspended":
      case "banned": return "p-tag-danger";
      case "resolved": return "p-tag-success";
      default: return "p-tag-secondary";
    }
  }

  getReasonSeverity(reason: string): string {
    switch (reason?.toLowerCase()) {
      case "harassment":
      case "bullying":
      case "inappropriate_content": return "p-tag-danger";
      case "spam": return "p-tag-warning";
      case "impersonation": return "p-tag-danger";
      case "other": return "p-tag-secondary";
      default: return "p-tag-info";
    }
  }



  async deleteSelectedReports() {
    const selectedReports = this.allReports().filter(r => r.selected);
    if (selectedReports.length === 0) return;

    const deletedSelected = selectedReports.filter(r => r.deletedAt);
    const activeSelected = selectedReports.filter(r => !r.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Reports' : 'Delete Reports';
    const bodyHeader = isRestore ? 'Restore Selected Reports' : 'Delete Selected Reports';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted report(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted report(s) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected report(s)?`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      yesBtnCss,
      noBtnCss: "btn-secondary",
      noButtonText: "Cancel",
      width: "500px",
    });

    if (confirmed) {
      const selectedIds = this.allReports().filter(r => r.selected).map(r => r._id);

      this.loading.set(true);
      this.violationReportsService.bulkDelete(selectedIds).pipe(
        finalize(() => this.loading.set(false))
      ).subscribe({
        next: (res) => {
          this.toastr.success(res.data?.message || 'Reports deleted successfully');
          this.fetchReports(); // Refresh list
          this.fetchStats(); // Update stats
          this.allSelected.set(false);
          this.hasSelection.set(false);
        },
        error: (err) => {
          this.toastr.error(err?.error?.Errors?.message || 'Failed to delete reports');
        }
      });
    }
  }


  closeViewDetails(): void {
    this.selectedReport.set(null);
    this.isViewDetailsOpen.set(false);
  }

  onAction(action: string, report: IViolationReport) {
    if (action === 'view') {
      this.selectedReport.set(report);
      this.isViewDetailsOpen.set(true);
    }
  }
  toggleIncludeDeleted(): void {
    this.includeDeleted.set(!this.includeDeleted());
    this.currentPage.set(1);
    this.fetchReports();
  }

  onModalAction(event: ModerationActionPayload) {
    if (event.action === 'ban' || event.action === 'suspend' || event.action === 'active' || event.action === 'deactivate') {
      const suspendedUntilStr = event.suspendDays ? new Date(Date.now() + event.suspendDays * 24 * 60 * 60 * 1000).toISOString() : undefined;
      const mappedAccountStatus = event.action === 'ban' ? 'banned' : (event.action === 'suspend' ? 'suspended' : 'active');
      const isActive = event.action === 'active' || event.action === 'suspend'; // Suspended users are active=true
      
      this.loading.set(true);
      this.adminService.updateUserStatus(
        event.report.reportedUserId, 
        isActive, 
        mappedAccountStatus, 
        suspendedUntilStr,
        event.reason,
        event.sendEmail ?? true, 
        event.emailSubject,
        event.emailBody,
        event.suspendedServices
      ).subscribe({
        next: () => {
          if (event.action === 'ban' || event.action === 'suspend') {
             this.updateReportStatus(
                event.report._id,
                event.action === 'ban' ? 'banned' : 'suspended'
             );
          } else if (event.action === 'active' || event.action === 'deactivate') {
             this.updateReportStatus(event.report._id, 'resolved');
          }
        },
        error: (err) => {
          this.loading.set(false);
          this.toastr.error(err?.error?.message || 'Failed to update user status');
        }
      });
    } else if (event.action === 'review') {
      this.updateReportStatus(event.report._id, 'reviewed');
    }
  }

  updateReportStatus(reportId: string, status: 'pending' | 'reviewed' | 'resolved' | 'dismissed' | 'banned' | 'suspended') {
    this.loading.set(true);
    this.violationReportsService.updateStatus(reportId, { status }).pipe(
        finalize(() => this.loading.set(false))
    ).subscribe({
      next: () => {
        this.toastr.success('Report updated successfully');
        this.isViewDetailsOpen.set(false);
        this.fetchReports();
        this.fetchStats();
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Failed to update report status');
      }
    });
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortReportsLocal();
  }

  sortReportsLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.allReports.set([...this.originalReports()]);
      this.updatePagination();
      this.applyPage();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.allReports()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'user') {
        const nameA = `${a.reportedUserInfo?.firstName || ''} ${a.reportedUserInfo?.lastName || ''}`.toLowerCase();
        const nameB = `${b.reportedUserInfo?.firstName || ''} ${b.reportedUserInfo?.lastName || ''}`.toLowerCase();
        valA = nameA;
        valB = nameB;
      } else if (field === 'createdAt') {
        valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (field === 'category') {
        valA = (a.category || '').toLowerCase();
        valB = (b.category || '').toLowerCase();
      } else if (field === 'reports') {
        valA = Number(a.reportedUserInfo?.totalReports || 0);
        valB = Number(b.reportedUserInfo?.totalReports || 0);
      } else if (field === 'status') {
        valA = (a.status || '').toLowerCase();
        valB = (b.status || '').toLowerCase();
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.allReports.set(sorted);
    this.updatePagination();
    this.applyPage();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}